import {CacheKeyConfig, useGrpcRequest} from '@aboutyou/data-fetcher';
import {BasketService_GetMinimalStateLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/service.grpc.lazy';
import {GetMinimalStateResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/service.types';
// eslint-disable-next-line monorepo/no-internal-import
import {useReductionsStateRequestParameter} from '@aboutyou/logic-components/src/features/ReductionsState/useReductionsStateRequestParameter';
import {Log} from '@aboutyou/mlm';
import {createDataStore} from '../createDataStore';
import {usePersistSessionStateCallback} from '../sessionStore/session.hooks';
import {useFullSession} from '../userStore/useSession';
import {useBasketToken} from './BasketTokenProvider';

const useExpensiveBasket = () => {
  const session = useFullSession();
  const reductionsState = useReductionsStateRequestParameter();
  const persistSessionState = usePersistSessionStateCallback();
  const {setBasketTokenFetchFailed, basketTokenHash, token} = useBasketToken();

  const response = useGrpcRequest(
    BasketService_GetMinimalStateLazy,
    {reductionsState, session: session!, basketToken: token},
    {
      // it will fail without it as session can and will be null for first render
      disabled: !session,
      refetchToken: basketTokenHash,
      onSuccess: (minimalState?: GetMinimalStateResponse) => {
        if (!minimalState?.basketToken) {
          Log.unSampledLog('GetMinimalState: Got no basket token');
          setBasketTokenFetchFailed(); // this is to unblock requests which are waiting for the basket token
          return;
        }
        const hasItemsInBasket = Boolean(minimalState?.state.totalQuantity);
        // we want to delay persisting of the session state as much as possible for legal reasons
        // if an anonymous user doesn't have anything in the basket/basket/or is loggedIn, we don't need to persist the session-id
        if (hasItemsInBasket) {
          persistSessionState();
        }
      },
      onError: () => {
        setBasketTokenFetchFailed();
      },
      transformCacheKey: (config: CacheKeyConfig) => {
        return {
          ...config,
          params: {
            ...config.params,
            basketToken: undefined,
          },
        };
      },
    },
  );

  return response;
};

const {Provider, useData: useBasketMinimalState} =
  createDataStore<GetMinimalStateResponse>({
    defaultValue: undefined,
    useQueryHook: useExpensiveBasket,
  });

export {Provider as MinimalStateProvider, useBasketMinimalState};
